import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, Subject, switchMap, tap } from 'rxjs';

import { BROUTER_MAP_CONFIG } from '../config/brouter-map.config';
import {
  BRouterSegment,
  defaultRoutingOptions,
  LatLng,
  RoutingOptions,
  Waypoint
} from '../models/brouter-map.model';
import { BrouterRouterService } from './brouter-router.service';
import { formatLatLng } from './brouter-url';
import {
  flagsAfterAppend,
  flagsAfterInsert,
  flagsAfterRemove,
  flagsAfterReverse,
  normaliseFlags
} from './route-operations';
import { RoutingError } from './routing-error';
import { calcTrackStats } from './track-stats';

let waypointSequence = 0;
const nextWaypointId = (): string => `wp-${++waypointSequence}`;

export const createWaypoint = (latLng: LatLng, name?: string): Waypoint => ({
  id: nextWaypointId(),
  lat: latLng.lat,
  lng: latLng.lng,
  ...(name ? { name } : {})
});

/**
 * Holds the edited route and keeps it in sync with the engine.
 *
 * Replaces `BR.Routing` from the legacy client. Routed legs are cached by their
 * endpoints and routing options, so dragging one waypoint only re-requests the two
 * legs that touch it rather than the whole track.
 *
 * Provided per map instance rather than in root, because two maps can be open at once
 * (the planner and the read-only track preview).
 */
@Injectable()
export class RouteEditorService {
  private readonly router = inject(BrouterRouterService);
  private readonly config = inject(BROUTER_MAP_CONFIG);
  private readonly destroyRef = inject(DestroyRef);

  readonly waypoints = signal<Waypoint[]>([]);
  /** One flag per leg, so always `waypoints.length - 1` entries. */
  readonly beelineFlags = signal<boolean[]>([]);
  readonly segments = signal<(BRouterSegment | null)[]>([]);
  readonly options = signal<RoutingOptions>(defaultRoutingOptions(this.config.profiles[0]));
  readonly isRouting = signal(false);
  readonly error = signal<RoutingError | null>(null);

  readonly stats = computed(() => calcTrackStats(this.segments(), this.beelineFlags()));
  readonly canRoute = computed(() => this.waypoints().length >= 2);

  /**
   * The routed geometry of each leg, in leg order.
   *
   * Kept separate from {@link trackLatLngs} so the map can render one polyline per leg
   * and therefore knows which leg a click landed on.
   */
  readonly legPaths = computed<LatLng[][]>(() => {
    const waypoints = this.waypoints();
    const segments = this.segments();
    const paths: LatLng[][] = [];

    for (let i = 0; i < Math.max(0, waypoints.length - 1); i++) {
      const segment = segments[i];
      if (segment) {
        paths.push(segment.geometry.coordinates.map(([lng, lat]) => ({ lat, lng })));
      } else if (this.beelineFlags()[i] && waypoints[i] && waypoints[i + 1]) {
        // A beeline leg is drawn straight between its endpoints.
        paths.push([
          { lat: waypoints[i].lat, lng: waypoints[i].lng },
          { lat: waypoints[i + 1].lat, lng: waypoints[i + 1].lng }
        ]);
      } else {
        paths.push([]);
      }
    }

    return paths;
  });

  /** The full track as a flat coordinate list, for `fitBounds`. */
  readonly trackLatLngs = computed<LatLng[]>(() => this.legPaths().flat());

  /**
   * Elevation per point of {@link trackLatLngs}, in metres, or `null` where the engine
   * returned none. BRouter carries elevation as the third GeoJSON coordinate component.
   */
  readonly trackElevations = computed<(number | null)[]>(() => {
    const waypoints = this.waypoints();
    const segments = this.segments();
    const flags = this.beelineFlags();
    const result: (number | null)[] = [];

    for (let i = 0; i < Math.max(0, waypoints.length - 1); i++) {
      const segment = segments[i];
      if (segment) {
        for (const coordinate of segment.geometry.coordinates) {
          result.push(coordinate.length > 2 ? coordinate[2] : null);
        }
      } else if (flags[i] && waypoints[i] && waypoints[i + 1]) {
        result.push(null, null);
      }
    }

    return result;
  });

  private readonly rerouteTrigger = new Subject<void>();
  private readonly segmentCache = new Map<string, BRouterSegment>();

  constructor() {
    this.rerouteTrigger
      .pipe(
        // Cancels legs still in flight whenever the route changes again.
        switchMap(() => {
          const waypoints = this.waypoints();
          const flags = this.beelineFlags();
          const options = this.options();

          if (waypoints.length < 2) {
            this.segments.set([]);
            this.isRouting.set(false);
            return of(null);
          }

          this.isRouting.set(true);
          this.error.set(null);

          return this.routeUsingCache(waypoints, flags, options).pipe(
            tap(segments => {
              this.segments.set(segments);
              this.isRouting.set(false);
            }),
            catchError((error: RoutingError) => {
              this.error.set(error);
              this.isRouting.set(false);
              return of(null);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  setWaypoints(waypoints: Waypoint[], beelineFlags?: boolean[]): void {
    this.waypoints.set(waypoints);
    this.beelineFlags.set(normaliseFlags(beelineFlags ?? [], waypoints.length));
    this.reroute();
  }

  /** `asBeeline` draws the new leg as a straight line instead of routing it. */
  addWaypoint(latLng: LatLng, name?: string, asBeeline = false): void {
    this.waypoints.update(current => [...current, createWaypoint(latLng, name)]);
    this.beelineFlags.update(flags => flagsAfterAppend(flags, this.waypoints().length, asBeeline));
    this.reroute();
  }

  /** Used when a leg is dragged, which splits it into two. */
  insertWaypointAt(index: number, latLng: LatLng): void {
    this.waypoints.update(current => {
      const next = [...current];
      next.splice(index, 0, createWaypoint(latLng));
      return next;
    });
    this.beelineFlags.update(flags => flagsAfterInsert(flags, index, this.waypoints().length));
    this.reroute();
  }

  moveWaypoint(id: string, latLng: LatLng): void {
    this.waypoints.update(current =>
      current.map(wp => (wp.id === id ? { ...wp, lat: latLng.lat, lng: latLng.lng } : wp))
    );
    this.reroute();
  }

  removeWaypoint(id: string): void {
    const index = this.waypoints().findIndex(wp => wp.id === id);
    if (index === -1) {
      return;
    }
    this.waypoints.update(current => current.filter(wp => wp.id !== id));
    this.beelineFlags.update(flags => flagsAfterRemove(flags, index, this.waypoints().length));
    this.reroute();
  }

  deleteLastPoint(): void {
    const waypoints = this.waypoints();
    if (waypoints.length === 0) {
      return;
    }
    this.removeWaypoint(waypoints[waypoints.length - 1].id);
  }

  /** Reverses both the waypoints and the per-leg beeline flags, which are leg-ordered. */
  reverse(): void {
    this.waypoints.update(current => [...current].reverse());
    this.beelineFlags.update(flagsAfterReverse);
    this.reroute();
  }

  toggleBeelineAt(legIndex: number): void {
    this.beelineFlags.update(flags => {
      const next = normaliseFlags(flags, this.waypoints().length);
      next[legIndex] = !next[legIndex];
      return next;
    });
    this.reroute();
  }

  clear(): void {
    this.waypoints.set([]);
    this.beelineFlags.set([]);
    this.segments.set([]);
    this.error.set(null);
  }

  setOptions(options: Partial<RoutingOptions>): void {
    this.options.update(current => ({ ...current, ...options }));
    // Routing options invalidate every cached leg.
    this.segmentCache.clear();
    this.reroute();
  }

  reroute(): void {
    this.rerouteTrigger.next();
  }

  /** Serialised route state, used to restore the map after it is torn down. */
  snapshot(): string {
    return this.router.serialiseRouteState(this.waypoints(), this.options(), this.beelineFlags());
  }

  restore(snapshot: string): void {
    const state = this.router.deserialiseRouteState(snapshot);
    if (!state.lonlats?.length) {
      return;
    }
    if (state.profile || state.alternative !== undefined) {
      this.options.update(current => ({
        ...current,
        ...(state.profile ? { profile: state.profile } : {}),
        ...(state.alternative !== undefined ? { alternative: state.alternative } : {})
      }));
    }
    this.setWaypoints(
      state.lonlats.map(ll => createWaypoint(ll)),
      state.beelineFlags
    );
  }

  private routeUsingCache(
    waypoints: readonly Waypoint[],
    flags: readonly boolean[],
    options: RoutingOptions
  ) {
    const legsToRequest: LatLng[][] = [];
    const legIndexes: number[] = [];
    const resolved: (BRouterSegment | null)[] = [];

    for (let i = 0; i < waypoints.length - 1; i++) {
      if (flags[i]) {
        resolved[i] = null;
        continue;
      }
      const cached = this.segmentCache.get(this.cacheKey(waypoints[i], waypoints[i + 1], options));
      if (cached) {
        resolved[i] = cached;
      } else {
        resolved[i] = null;
        legsToRequest.push([waypoints[i], waypoints[i + 1]]);
        legIndexes.push(i);
      }
    }

    if (legsToRequest.length === 0) {
      return of(resolved);
    }

    // Request only the uncached legs, then merge them back into their positions.
    return this.requestLegs(legsToRequest, options).pipe(
      switchMap(fetched => {
        fetched.forEach((segment, n) => {
          const legIndex = legIndexes[n];
          resolved[legIndex] = segment;
          if (segment) {
            this.segmentCache.set(
              this.cacheKey(waypoints[legIndex], waypoints[legIndex + 1], options),
              segment
            );
          }
        });
        return of(resolved);
      })
    );
  }

  private requestLegs(legs: LatLng[][], options: RoutingOptions) {
    const flat: LatLng[] = [];
    for (const [from, to] of legs) {
      flat.push(from, to);
    }
    // `routeSegments` walks consecutive pairs, so mark the joins between legs as beelines
    // to stop it routing from one leg's end to the next leg's start.
    const joinFlags: boolean[] = [];
    for (let i = 0; i < legs.length; i++) {
      joinFlags.push(false);
      if (i < legs.length - 1) {
        joinFlags.push(true);
      }
    }

    return this.router
      .routeSegments(flat, options, joinFlags)
      .pipe(switchMap(segments => of(segments.filter((_, i) => i % 2 === 0))));
  }

  private cacheKey(from: LatLng, to: LatLng, options: RoutingOptions): string {
    return [
      formatLatLng(from),
      formatLatLng(to),
      options.profile,
      options.alternative,
      options.nogos.length,
      options.polylines.length,
      options.polygons.length
    ].join('~');
  }
}
